import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import _ from 'underscore';

import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { SingleMonthCalendar } from '@bingads-webui-react/single-month-calendar';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { compareCivilMonth, parseCivilDate, translateLegacyToIANATimezone } from '@bingads-webui-universal/temporal-utitlities';
import { Callout, DefaultButton, DirectionalHint } from '@fluentui/react';

import { getBudgetCenterMonthPickerStyle } from '../styles';

const isoDateFormat = 'yyyy-MM-dd';

const MonthPickerBase = ({
  i18n,
  serverContext,
  [WaterfallFilterType.BudgetCenterMonthFilter]: monthFilter,
  updateWaterfallFilter,
}) => {
  const classes = useDefaultStyles(getBudgetCenterMonthPickerStyle);
  const buttonClass = 'budget-center-month-picker-button';
  const timeZone = _.get(serverContext, ['CurrentAccount', 'TimeZone']);
  const activity = useContext(ActivityContext);
  const civilDateToday = useMemo(() => {
    const isoDateString = i18n.formatDate(new Date(), {
      timeZone: translateLegacyToIANATimezone(timeZone),
      raw: isoDateFormat,
    });
    return parseCivilDate({
      i18n,
      dateString: isoDateString,
      dateFormat: isoDateFormat,
    });
  }, [i18n, timeZone]);
  const earliestDate = _.get(monthFilter, 'earliestDate');
  const selectedDate = _.get(monthFilter, 'selectedDate', civilDateToday);
  const minDate = useMemo(() => earliestDate, [earliestDate]);
  const maxDate = useMemo(() => civilDateToday, [civilDateToday]);

  useEffect(() => {
    updateWaterfallFilter({
      filterType: WaterfallFilterType.BudgetCenterMonthFilter,
      filter: { civilDateToday, selectedDate: civilDateToday },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [navigatedDate, setNavigatedDate] = useState(civilDateToday);
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const monthText = useMemo(() => i18n.formatCivilDate(selectedDate, { skeleton: 'MMMM' }), [i18n, selectedDate]);
  const isMonthPickerButtonDisabled = useMemo(() => !minDate, [minDate]);
  const isPrevMonthButtonDisabled = useMemo(() => !minDate || compareCivilMonth(selectedDate, minDate) === 0, [minDate, selectedDate]);
  const isNextMonthButtonDisabled = useMemo(() => !minDate || compareCivilMonth(selectedDate, maxDate) === 0, [minDate, maxDate, selectedDate]);
  const toggleIsCalloutVisible = () => setIsCalloutVisible(visible => !visible);
  const updateSelectedDate = (date) => {
    updateWaterfallFilter({
      filterType: WaterfallFilterType.BudgetCenterMonthFilter,
      filter: {
        ...monthFilter,
        selectedDate: date,
      },
    });
  };
  const logTrace = ({ date, api }) => {
    if (activity) {
      activity.trace(date.toString(), api);
    }
  };
  const onSelectDate = (date) => {
    logTrace({ date, api: 'onSelectDate' });
    updateSelectedDate(date);
    setIsCalloutVisible(false);
  };
  const onPrevMonthClick = () => {
    const newDate = selectedDate.plus({ months: -1 });
    if (compareCivilMonth(newDate, minDate) >= 0) {
      logTrace({ date: newDate, api: 'onPrevMonthClick' });
      updateSelectedDate(newDate);
      setNavigatedDate(newDate);
    }
  };
  const onNextMonthClick = () => {
    const newDate = selectedDate.plus({ months: 1 });
    if (compareCivilMonth(newDate, maxDate) <= 0) {
      logTrace({ date: newDate, api: 'onNextMonthClick' });
      updateSelectedDate(newDate);
      setNavigatedDate(newDate);
    }
  };

  useEffect(() => {
    if (selectedDate.year !== navigatedDate.year) {
      setNavigatedDate(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  return (
    <div className={classnames('budget-center-month-picker-container', classes.monthPickerContainer)}>
      <div className={classnames('month-picker-section', classes.monthPickerSection)}>
        <div className="month-picker-button">
          <DefaultButton
            text={
              <div className={classnames('month-picker-button-text', classes.monthPickerButtonText)}>
                <span className={classnames('month-text', classes.monthText)}>
                  {`${monthText} ${selectedDate.year}`}
                </span>
                <Glyphicon
                  glyph="ba-Calendar"
                  className={classnames('calendar-icon', classes.calendarIcon)}
                />
              </div>
            }
            className={classnames(buttonClass, classes.monthPickerButton, isCalloutVisible ? 'dialog-visible' : 'dialog-invisible')}
            onClick={toggleIsCalloutVisible}
            disabled={isMonthPickerButtonDisabled}
          />
        </div>
        <div className={classnames('navigation-icons', classes.navigationIcons)}>
          <button
            type="button"
            onClick={onPrevMonthClick}
            className={classnames('prev-month-button', classes.navigationIconButtonWrapper)}
            disabled={isPrevMonthButtonDisabled}
          >
            <Glyphicon
              glyph="ba-ChevronLeft"
              className={classes.navigationIcon}
            />
          </button>
          <button
            type="button"
            onClick={onNextMonthClick}
            className={classnames('next-month-button', classes.navigationIconButtonWrapper)}
            disabled={isNextMonthButtonDisabled}
          >
            <Glyphicon
              glyph="ba-ChevronRight"
              className={classes.navigationIcon}
            />
          </button>
        </div>
      </div>
      {isCalloutVisible ? (
        <Callout
          role="dialog"
          target={`.${buttonClass}`}
          isBeakVisible={false}
          onDismiss={toggleIsCalloutVisible}
          directionalHint={DirectionalHint.bottomCenter}
          className={classnames('month-picker-callout', classes.monthPickerCallout)}
        >
          <SingleMonthCalendar
            i18n={i18n}
            minDate={minDate}
            maxDate={maxDate}
            today={civilDateToday}
            navigatedDate={navigatedDate}
            selectedDate={selectedDate}
            onSelectDate={onSelectDate}
            onNavigateDate={setNavigatedDate}
            isMonthRangePicker
          />
        </Callout>
      ) : null}
    </div>
  );
};

MonthPickerBase.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    formatDate: PropTypes.func,
    formatCivilDate: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      TimeZone: PropTypes.number,
    }),
  }).isRequired,
  selectedParent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  updateWaterfallFilter: PropTypes.func,
};

MonthPickerBase.defaultProps = {
  updateWaterfallFilter: _.noop,
};

export const MonthPicker = _.compose(
  withI18nConsumer,
  withServerContext,
  withGlobalFilter
)(props => (
  <MonthPickerBase {...props} />
));
